import Modal from "components/Modal/Modal";
import { t } from "@lingui/core/macro";
import {
  ModalBody,
  PartialSanityImageType
} from "@silversea-ssc/ui-components-react";
import * as styles from "./WebQuoteModal.module.scss";
import React, { useState } from "react";
import { isPresent } from "src/utils/checks";
import WebQuoteModalContentWithImage from "components/WebQuoteModal/WebQuoteModalContentWithImage";
import { SendQuoteSectionIntro } from "components/WebQuoteModal/SendQuoteSectionIntro/SendQuoteSectionIntro";
import { WebQuoteForm } from "components/WebQuoteModal/WebQuoteForm/WebQuoteForm";
import FriendlyCaptchaStatusProvider from "components/WebQuoteModal/FriendlyCaptcha/FriendlyCaptchaProvider";
import { ThankYouModalStep } from "components/WebQuoteModal/ThankYouModalStep/ThankYouModalStep";
import { WebQuoteError } from "components/WebQuoteModal/utils";
import { ErrorModalStep } from "components/WebQuoteModal/ErrorModalStep/ErrorModalStep";

type Props = {
  departureDate?: string;
  arrivalDate?: string;
  duration?: number;
  shipName?: string;
  cruiseName: string;
  cruiseCode: string;
  image?: PartialSanityImageType;
  onRequestClose: () => void;
  quoteApi: string | undefined;
  quoteProcessId: string;
  fycRedirectUrl?: string;
};

export type WebQuoteFormState = {
  submitted: boolean;
  validationError: WebQuoteError | undefined;
};

const WebQuoteModal: React.FC<React.PropsWithChildren<Props>> = ({
  departureDate,
  arrivalDate,
  duration,
  shipName,
  cruiseName,
  cruiseCode,
  image,
  onRequestClose,
  quoteApi,
  quoteProcessId,
  fycRedirectUrl
}) => {
  const [formState, setFormState] = useState<WebQuoteFormState>({
    submitted: false,
    validationError: undefined
  });
  if (!isPresent(quoteApi)) return null;

  const handleFormSubmittedChange = (formState: WebQuoteFormState) => {
    setFormState(formState);
  };

  return (
    <Modal
      title={t`Save this Quote`}
      size="medium"
      modal={{
        isOpen: true,
        onRequestClose: onRequestClose,
        shouldCloseOnOverlayClick: false
      }}
      dataContentName="web-quote-modal"
      headerClassName={styles.modalHeader}
    >
      {!formState.submitted && (
        <ModalBody variant="full" paddingTop={false} paddingBottom={false}>
          <FriendlyCaptchaStatusProvider>
            <WebQuoteModalContentWithImage
              departureDate={departureDate}
              arrivalDate={arrivalDate}
              duration={duration}
              shipName={shipName}
              cruiseName={cruiseName}
              cruiseCode={cruiseCode}
              image={image}
            >
              <>
                <SendQuoteSectionIntro />
                <WebQuoteForm
                  cruiseCode={cruiseCode}
                  quoteProcessId={quoteProcessId}
                  quoteApi={quoteApi}
                  setFormSubmitted={handleFormSubmittedChange}
                  validationError={formState.validationError}
                />
              </>
            </WebQuoteModalContentWithImage>
          </FriendlyCaptchaStatusProvider>
        </ModalBody>
      )}
      {formState.submitted && formState.validationError === undefined && (
        <ModalBody variant="full" className={styles.thankYouBody}>
          <ThankYouModalStep
            fycRedirectUrl={fycRedirectUrl}
            onRequestClose={onRequestClose}
          />
        </ModalBody>
      )}
      {formState.submitted && formState.validationError === "fatal" && (
        <ModalBody variant="full" className={styles.thankYouBody}>
          <ErrorModalStep
            onRequestClose={onRequestClose}
            fycRedirectUrl={fycRedirectUrl}
          />
        </ModalBody>
      )}
    </Modal>
  );
};

export default WebQuoteModal;
